// @flow
// Photos are shrunk on the device before they are uploaded: a phone photo (12 to 48 MP, 3 to 15 MB) leaves as a
// JPEG of at most 2560 px, about 1 MB, so the upload is faster and the backend's job (large + thumb) gets a
// small file. Anything unexpected uploads the original file untouched, as before.

// Long side of the uploaded image (the file the doctor downloads). The backend's lightbox "large" is 1024 px.
export const UPLOAD_MAX_DIMENSION = 2560;
export const UPLOAD_JPEG_QUALITY = 0.9;
// The local preview shown on the tile while uploading, and on the attachment until the page reloads: a square
// like the backend's 175 px thumb (resize_to_fill), at twice its size for sharp retina screens
export const PREVIEW_SIZE = 350;
const PREVIEW_JPEG_QUALITY = 0.8;
// iOS refuses canvases over 16.7 MP (4096²): a larger halving step is skipped
const MAX_CANVAS_AREA = 4096 * 4096;
// A decode or an encode that never answers must not hold the uploads queued behind it
export const PREPARE_TIMEOUT_MS = 20 * 1000;

// A 2×1 JPEG tagged EXIF orientation 6 (rotate 90°): a browser that applies the tag decodes it 1×2
const ROTATED_JPEG = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAAAAAD/2wBDAFA3PEY8MlBGQUZaVVBfeMiCeG5uePWvuZHI////////////////////////////////////////////////////2wBDAVVaWnhpeOuCguv/////////////////////////////////////////////////////////////////////////wAARCAABAAIDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAT/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFAEBAAAAAAAAAAAAAAAAAAAAAP/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/ALQAf//Z';

export type ImageFormat = 'jpeg' | 'png' | 'other';

export type PreparedImage = {
  // The bytes to upload in place of the file; null uploads the file itself
  blob: ?Blob,
  width: ?number,
  height: ?number,
  // Object URL of a small JPEG of the photo; null when the browser could not draw it
  previewUrl: ?string,
};

// What the bytes are, whatever the name says: a HEIC renamed by normalizeHeicFile says jpeg
export const sniffImageFormat = (bytes: Uint8Array): ImageFormat => {
  if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) return 'jpeg';
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) return 'png';
  return 'other';
};

// The fitted size, never upscaled
export const fitWithin = (width: number, height: number, maxDimension: number): [number, number] => {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return [Math.max(1, Math.round(width * scale)), Math.max(1, Math.round(height * scale))];
};

const canPrepare = (): boolean => !!window.URL
  && typeof window.URL.createObjectURL === 'function'
  && typeof window.HTMLCanvasElement === 'function'
  && typeof window.HTMLCanvasElement.prototype.toBlob === 'function';

const loadImage = (url: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => (image.naturalWidth > 0 ? resolve(image) : reject(new Error('empty image')));
  image.onerror = () => reject(new Error('undecodable image'));
  image.src = url;
});

let appliesOrientation: ?Promise<boolean> = null;

// Every WebView the app supports applies it (Chrome 81, iOS 13.4); one that does not would upload photos sideways
const browserAppliesExifOrientation = (): Promise<boolean> => {
  if (!appliesOrientation) {
    appliesOrientation = loadImage(ROTATED_JPEG)
      .then((image) => image.naturalWidth === 1 && image.naturalHeight === 2, () => {
        // A failed decode says nothing about the browser: ask again for the next photo
        appliesOrientation = null;
        return false;
      });
  }
  return appliesOrientation;
};

const readHead = (file: Blob): Promise<Uint8Array> => {
  const head = file.slice(0, 4);
  const buffer = typeof head.arrayBuffer === 'function' ? head.arrayBuffer() : new Response(head).arrayBuffer();
  return buffer.then((bytes) => new Uint8Array(bytes));
};

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

// Safari keeps a canvas' memory until it is sized down, and caps the total
const releaseCanvas = (canvas: HTMLCanvasElement) => {
  canvas.width = 0;
  canvas.height = 0;
};

const context2d = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('no 2d context');
  return context;
};

// Halving steps first: one big step samples too few source pixels, and fine lines (text, skin texture) alias. Always
// the whole source: a source rectangle on an EXIF-rotated <img> was read unrotated by some Chromium builds.
const drawScaled = (source: HTMLImageElement | HTMLCanvasElement, sourceWidth: number, sourceHeight: number, width: number, height: number): HTMLCanvasElement => {
  const steps = [];
  let stepWidth = sourceWidth;
  let stepHeight = sourceHeight;
  while (stepWidth / 2 > width) {
    stepWidth = Math.round(stepWidth / 2);
    stepHeight = Math.round(stepHeight / 2);
    if (stepWidth * stepHeight <= MAX_CANVAS_AREA) steps.push([stepWidth, stepHeight]);
  }
  steps.push([width, height]);

  let drawn = null;
  steps.forEach(([w, h]) => {
    const canvas = createCanvas(w, h);
    try {
      const context = context2d(canvas);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(drawn || source, 0, 0, w, h);
    } finally {
      if (drawn) releaseCanvas(drawn);
    }
    drawn = canvas;
  });
  // $FlowFixMe: steps always ends with the target size, so something was drawn
  return drawn;
};

// Out of canvas memory, iOS draws nothing without an error: an untouched canvas is all transparent. The smoothed
// probe averages the whole canvas, so a thin signature on a transparent PNG still counts as drawn.
const isBlank = (canvas: HTMLCanvasElement): boolean => {
  const probe = createCanvas(16, 16);
  try {
    const context = context2d(probe);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(canvas, 0, 0, 16, 16);
    const { data } = context.getImageData(0, 0, 16, 16);
    for (let alpha = 3; alpha < data.length; alpha += 4) {
      if (data[alpha] !== 0) return false;
    }
    return true;
  } finally {
    releaseCanvas(probe);
  }
};

// JPEG has no transparency: transparent pixels would turn black
const flattenOnWhite = (canvas: HTMLCanvasElement) => {
  const context = context2d(canvas);
  context.globalCompositeOperation = 'destination-over';
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.globalCompositeOperation = 'source-over';
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> => new Promise((resolve, reject) => {
  canvas.toBlob((blob: Object) => {
    // A browser that cannot encode the type answers with a PNG instead
    if (blob && blob.size > 0 && blob.type === type) resolve(blob);
    else reject(new Error(`could not encode ${type}`));
  }, type, quality);
});

// Scaled until its short side is the preview's, then the centre square is cut from that canvas
const makePreview = async (source: HTMLImageElement | HTMLCanvasElement, width: number, height: number): Promise<string> => {
  const scale = Math.min(1, PREVIEW_SIZE / Math.min(width, height));
  const scaledWidth = Math.max(1, Math.round(width * scale));
  const scaledHeight = Math.max(1, Math.round(height * scale));
  const size = Math.min(scaledWidth, scaledHeight);
  const scaled = drawScaled(source, width, height, scaledWidth, scaledHeight);
  const square = createCanvas(size, size);
  try {
    context2d(square).drawImage(scaled, Math.round((scaledWidth - size) / 2), Math.round((scaledHeight - size) / 2), size, size, 0, 0, size, size);
    if (isBlank(square)) throw new Error('blank preview');
    flattenOnWhite(square);
    return URL.createObjectURL(await canvasToBlob(square, 'image/jpeg', PREVIEW_JPEG_QUALITY));
  } finally {
    releaseCanvas(scaled);
    releaseCanvas(square);
  }
};

type Size = { width: ?number, height: ?number };

// The size is what is known when it gives up: the file's own (camera photos carry it), or the decoded one
const unprepared = ({ width, height }: Size): PreparedImage => ({
  blob: null, width, height, previewUrl: null,
});

// Never rejects: a photo that cannot be prepared uploads as it is
const prepare = async (file: Object, known: Size): Promise<PreparedImage> => {
  if (!canPrepare()) return unprepared(known);

  let url = null;
  let image = null;
  let canvas = null;
  try {
    url = URL.createObjectURL(file);
    const [decoded, head, upright] = await Promise.all([loadImage(url), readHead(file), browserAppliesExifOrientation()]);
    image = decoded;
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    known.width = width;
    known.height = height;
    if (!upright) return unprepared(known);

    const sourceFormat = sniffImageFormat(head);
    // A renamed HEIC (decodable in Safari) leaves as a real JPEG, so its name tells the truth
    const outputFormat = sourceFormat === 'other' ? (String(file.type).toLowerCase() === 'image/png' ? 'png' : 'jpeg') : sourceFormat;
    const [targetWidth, targetHeight] = fitWithin(width, height, UPLOAD_MAX_DIMENSION);

    let blob = null;
    if (targetWidth < width || targetHeight < height || sourceFormat === 'other') {
      canvas = drawScaled(image, width, height, targetWidth, targetHeight);
      if (isBlank(canvas)) throw new Error('blank canvas');
      if (outputFormat === 'jpeg') flattenOnWhite(canvas);
      const encoded = await canvasToBlob(canvas, `image/${outputFormat}`, UPLOAD_JPEG_QUALITY);
      // Re-encoding that saves no bytes only costs quality
      if (sourceFormat === 'other' || encoded.size < file.size) blob = encoded;
    }

    const previewUrl = await makePreview(canvas || image, canvas ? targetWidth : width, canvas ? targetHeight : height)
      .catch(() => null);
    return {
      blob,
      width: blob ? targetWidth : width,
      height: blob ? targetHeight : height,
      previewUrl,
    };
  } catch (error) {
    return unprepared(known);
  } finally {
    if (url) URL.revokeObjectURL(url);
    if (canvas) releaseCanvas(canvas);
    // Lets WebKit drop the full-size bitmap now rather than at the next garbage collection
    if (image) image.removeAttribute('src');
  }
};

// Resolves when the promise settles or after ms, whichever comes first
const settledWithin = (promise: Promise<mixed>, ms: number): Promise<void> => new Promise((resolve) => {
  const timer = setTimeout(resolve, ms);
  const settle = () => {
    clearTimeout(timer);
    resolve();
  };
  promise.then(settle, settle);
});

let queue: Promise<mixed> = Promise.resolve();
let gaveUp = false;

// One photo at a time: ten 48 MP photos decoded at once would exhaust a phone's WebView memory. A photo slower than
// timeoutMs uploads as it is, and so do the photos after it for the rest of the session: this device is too slow, or
// its decode hangs, and the next photo must neither wait nor decode alongside one that may still be running.
export const prepareImageForUpload = (file: Object, timeoutMs?: number = PREPARE_TIMEOUT_MS): Promise<PreparedImage> => {
  const known = { width: file.width, height: file.height };
  const result = queue.then(() => {
    if (gaveUp) return unprepared(known);

    const work = prepare(file, known);
    let done = null;
    work.then((prepared) => { done = prepared; });
    return settledWithin(work, timeoutMs).then(() => {
      if (done) return done;
      gaveUp = true;
      // Too late: the original is already on its way, so the preview is dropped
      work.then((late) => {
        if (late.previewUrl) URL.revokeObjectURL(late.previewUrl);
      });
      return unprepared(known);
    });
  });
  queue = result.catch(() => {});
  return result;
};

// Local previews of the attachments uploaded in this session, by attachment id: the tile shows the photo at once,
// instead of polling for a thumb the backend job may not have made yet. Only the latest ones are kept, as each
// holds about 1 MB for as long as the app stays open; an older one shows the backend's versions again.
export const MAX_LOCAL_PREVIEWS = 30;
const localPreviews: Map<string, { thumbUrl: string, largeUrl: string }> = new Map();

export const forgetLocalPreview = (attachmentId: number | string) => {
  const preview = localPreviews.get(String(attachmentId));
  if (!preview) return;
  localPreviews.delete(String(attachmentId));
  URL.revokeObjectURL(preview.thumbUrl);
  URL.revokeObjectURL(preview.largeUrl);
};

export const rememberLocalPreview = (attachmentId: number | string, thumbUrl: string, largeUrl: string) => {
  localPreviews.set(String(attachmentId), { thumbUrl, largeUrl });
  while (localPreviews.size > MAX_LOCAL_PREVIEWS) {
    const oldestId = localPreviews.keys().next().value;
    if (oldestId == null) break;
    forgetLocalPreview(oldestId);
  }
};

export const localPreviewFor = (attachmentId: ?(number | string)): ?{ thumbUrl: string, largeUrl: string } => (
  attachmentId == null ? null : localPreviews.get(String(attachmentId))
);
